import React, { useCallback, useEffect, useRef } from 'react';

/**
 * The Aerveil message thread, ported from the aerveil.ai landing page
 * (components/site/ChatThread.tsx in the aerveil-website repo) and converted
 * from TypeScript. Kept deliberately close to the original so the portfolio and
 * the product site show the same artifact rather than two design exercises.
 *
 * Notes carried over from the original, because they are load-bearing:
 *
 *  - Dark theme on purpose. Contrast is #E9EDEF on #202C33, roughly 14:1.
 *  - Everything inside a bubble is set in the system UI face, never the brand
 *    typeface. Brand type in a bubble makes it a graphic; system type makes it
 *    read as a screenshot, which is the whole job.
 *  - Sizing is in container units, so the thread scales with whatever box it is
 *    dropped into and characters-per-line stays constant. Requires
 *    `container-type: inline-size` on the wrapper (`.chat-scope` in index.css).
 *  - No delivery ticks on received bubbles. WhatsApp only shows ticks on
 *    messages you sent, and this audience uses WhatsApp daily.
 *  - Messages arrive whole, never typed character by character. Real messages do
 *    not do that, and a typewriter effect is the loudest generic-AI-site tell.
 */

const INK = '#E9EDEF';
const MUTED = '#8696A0';
const LINK = '#53BDEB';
const BUBBLE_IN = '#202C33';
const BUBBLE_OUT = '#005C4B';
const CHROME = '#1F2C34';

const AFTER_SPRAY = ['spray', 'typingFrost', 'frost'];

const WELCOME = {
  head: 'Welcome to Aerveil',
  paras: [
    'Stoneridge is set up: 14 blocks across three vineyards.',
    'From tonight I’ll message you when a frost night or a spray window needs a decision. Nothing in between.',
    'Ask me about any block, any time.',
  ],
  time: '9:20 AM',
};

const SPRAY = {
  head: 'Spray window: Blocks 3, 5 and 7',
  paras: [
    'Tomorrow, 6:00 to 11:20 AM.\nMildew pressure has been high six days running.\nWind stays under 8 mph and it’s dry through the afternoon.',
    'Wind comes up at 11:20 and closes it.\nCrew can be back in Thursday morning.',
  ],
  linkLabel: 'Full window:',
  url: 'aerveil.ai/v/stoneridge',
  time: '7:05 AM',
};

const FROST = {
  head: 'Frost tonight: Block 7',
  paras: [
    'Start the machines around 2:40 AM.\nBlock 7 (Chardonnay, first leaf) is headed to\n26°F by 3 AM. Damage starts at 27°.',
    'Blocks 3, 4 and 9 are clear tonight.',
  ],
  linkLabel: 'Full night:',
  url: 'aerveil.ai/v/stoneridge/blk7',
  time: '9:12 PM',
};

const ChatThread = ({ phase, className = '' }) => {
  const scroller = useRef(null);
  const raf = useRef(null);
  const held = useRef(null);

  const glide = useCallback((dur) => {
    const el = scroller.current;
    if (!el || held.current) return;
    const from = el.scrollTop;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
      el.scrollTop = from + (el.scrollHeight - el.clientHeight - from) * e;
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    if (raf.current !== null) cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => glide(750), 90);
    return () => clearTimeout(id);
  }, [phase, glide]);

  // The auto-scroll yields the instant anyone touches it and stays out of the
  // way for six seconds. Automation that fights the reader is the tell.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const yieldToUser = () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
      if (held.current) clearTimeout(held.current);
      held.current = window.setTimeout(() => {
        held.current = null;
      }, 6000);
    };
    const opts = { passive: true };
    el.addEventListener('wheel', yieldToUser, opts);
    el.addEventListener('touchstart', yieldToUser, opts);
    el.addEventListener('pointerdown', yieldToUser, opts);
    return () => {
      el.removeEventListener('wheel', yieldToUser);
      el.removeEventListener('touchstart', yieldToUser);
      el.removeEventListener('pointerdown', yieldToUser);
      if (raf.current !== null) cancelAnimationFrame(raf.current);
      if (held.current) clearTimeout(held.current);
    };
  }, []);

  return (
    <div className={`chat-root flex h-full flex-col ${className}`} style={{ background: '#0B141A' }}>
      <Header />

      <div
        ref={scroller}
        tabIndex={0}
        aria-label="Messages from Aerveil"
        className="flex flex-1 flex-col overflow-y-auto overscroll-contain px-[0.55em] py-[0.5em]"
      >
        {/* Anchored at the top, not the bottom. Bottom-anchoring meant every
            arrival shoved the whole thread upward and the welcome never held
            still; the reader is watching for the new message appearing beneath
            what is already there. */}
        <div>
          <Divider>March 28</Divider>
          <Bubble msg={WELCOME} />

          <Reveal show={phase !== 'welcome'}>
            <Divider>Today</Divider>
          </Reveal>
          <Reveal show={phase === 'typingSpray'}>
            <Typing />
          </Reveal>
          <Reveal show={AFTER_SPRAY.includes(phase)}>
            <Bubble msg={SPRAY} arriving />
          </Reveal>
          <Reveal show={phase === 'typingFrost'}>
            <Typing />
          </Reveal>
          <Reveal show={phase === 'frost'}>
            <Bubble msg={FROST} arriving />
          </Reveal>
        </div>
      </div>

      <ComposeBar />
    </div>
  );
};

/** Grid-row height transition, so the thread eases upward as each piece lands
 *  instead of snapping. */
const Reveal = ({ show, children }) => (
  <div
    className={`grid transition-[grid-template-rows] duration-[300ms] ease-out ${
      show ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
    }`}
  >
    <div className="overflow-hidden">{show ? children : null}</div>
  </div>
);

/** The bar you type in. Without it the thread reads as a transcript rather than
 *  an open conversation. Decorative only. */
const ComposeBar = () => (
  <div
    aria-hidden="true"
    className="flex shrink-0 items-center gap-[0.45em] px-[0.5em] py-[0.42em]"
    style={{ background: CHROME }}
  >
    <svg
      viewBox="0 0 24 24"
      className="h-[1.25em] w-[1.25em] shrink-0"
      fill="none"
      stroke={MUTED}
      strokeWidth="1.9"
      strokeLinecap="round"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>

    <span
      className="flex flex-1 items-center gap-[0.4em] rounded-full px-[0.7em] py-[0.3em]"
      style={{ background: '#2A3942' }}
    >
      <span className="flex-1 truncate text-[0.95em] leading-[1.5]" style={{ color: MUTED }}>
        Message
      </span>
      <svg
        viewBox="0 0 24 24"
        className="h-[1.05em] w-[1.05em] shrink-0"
        fill="none"
        stroke={MUTED}
        strokeWidth="1.7"
      >
        <path d="M3 8.5h3.2L8 6.2h8L17.8 8.5H21v10H3z" strokeLinejoin="round" />
        <circle cx="12" cy="13" r="3.1" />
      </svg>
    </span>

    <span
      className="flex h-[1.9em] w-[1.9em] shrink-0 items-center justify-center rounded-full"
      style={{ background: '#00A884' }}
    >
      <svg
        viewBox="0 0 24 24"
        className="h-[1.05em] w-[1.05em]"
        fill="none"
        stroke="#0B141A"
        strokeWidth="1.9"
        strokeLinecap="round"
      >
        <rect x="9.2" y="2.6" width="5.6" height="11" rx="2.8" />
        <path d="M5.5 11.4a6.5 6.5 0 0 0 13 0M12 17.9V21" />
      </svg>
    </span>
  </div>
);

const Header = () => (
  <div
    className="flex shrink-0 items-center gap-[0.6em] px-[0.6em] py-[0.5em]"
    style={{ background: CHROME }}
  >
    <svg viewBox="0 0 9 15" fill="none" aria-hidden="true" className="h-[0.95em] w-[0.6em] shrink-0">
      <path
        d="M7.5 1 1.5 7.5 7.5 14"
        stroke={INK}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>

    <span className="flex h-[2em] w-[2em] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#E0AD2A]">
      <img src="/img/aerveil-icon.png" alt="" className="h-[1.25em] w-[1.25em]" />
    </span>

    <span className="text-[1.02em] font-medium leading-tight" style={{ color: INK }}>
      Aerveil
    </span>
  </div>
);

const Divider = ({ children }) => (
  <div className="flex justify-center py-[0.55em]">
    <span
      className="rounded-[0.5em] px-[0.6em] py-[0.2em] text-[0.78em] uppercase tracking-[0.04em]"
      style={{ background: '#182229', color: MUTED }}
    >
      {children}
    </span>
  </div>
);

const Typing = () => (
  <div
    className="relative mb-[0.35em] ml-[0.5em] w-fit rounded-[0.6em] rounded-tl-none px-[0.9em] py-[0.75em]"
    style={{ background: BUBBLE_IN }}
  >
    <Tail />
    <span className="flex items-center gap-[0.2em]">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="chat-dot h-[0.42em] w-[0.42em] rounded-full"
          style={{ background: MUTED, animationDelay: `${i * 0.16}s` }}
        />
      ))}
    </span>
  </div>
);

/** The flare off the corner that makes a rectangle a bubble. */
const Tail = ({ side = 'in' }) => {
  const fill = side === 'in' ? BUBBLE_IN : BUBBLE_OUT;
  return (
    <span
      aria-hidden="true"
      className={`absolute top-0 h-0 w-0 ${side === 'in' ? 'left-[-0.55em]' : 'right-[-0.55em]'}`}
      style={
        side === 'in'
          ? { borderTop: `0.55em solid ${fill}`, borderLeft: '0.55em solid transparent' }
          : { borderTop: `0.55em solid ${fill}`, borderRight: '0.55em solid transparent' }
      }
    />
  );
};

const Bubble = ({ msg, arriving = false, side = 'in', ticks = false }) => {
  const incoming = side === 'in';
  return (
    <div className={incoming ? '' : 'flex justify-end'}>
      <div
        className={[
          'relative mb-[0.35em] w-fit max-w-[96%] rounded-[0.6em] px-[0.62em] pb-[0.3em] pt-[0.4em]',
          incoming ? 'ml-[0.5em] rounded-tl-none' : 'mr-[0.5em] rounded-tr-none',
          arriving ? 'msg-in' : '',
        ].join(' ')}
        style={{ background: incoming ? BUBBLE_IN : BUBBLE_OUT }}
      >
        <Tail side={side} />

        {msg.head && (
          <p className="text-[1em] font-semibold leading-[1.34]" style={{ color: INK }}>
            {msg.head}
          </p>
        )}

        {msg.paras.map((p, i) => (
          <p
            key={p.slice(0, 18)}
            className={`whitespace-pre-wrap text-[1em] leading-[1.34] ${
              i === 0 && !msg.head ? '' : 'mt-[0.7em]'
            }`}
            style={{ color: INK }}
          >
            {p}
          </p>
        ))}

        {msg.url && (
          <p className="mt-[0.7em] text-[1em] leading-[1.34]" style={{ color: INK }}>
            {msg.linkLabel}{' '}
            <span className="underline" style={{ color: LINK }}>
              {msg.url}
            </span>
          </p>
        )}

        <p
          className="mt-[0.2em] flex items-center justify-end gap-[0.2em] text-[0.74em] leading-none"
          style={{ color: MUTED }}
        >
          {msg.time}
          {ticks && (
            <svg
              viewBox="0 0 15 10"
              fill="none"
              stroke="#53BDEB"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="h-[0.9em] w-[1.35em]"
            >
              <path d="M1 5.4 3.4 8 8 2.2" />
              <path d="M6.4 5.4 8.8 8 13.4 2.2" />
            </svg>
          )}
        </p>
      </div>
    </div>
  );
};

export default ChatThread;
